import React, { useMemo, useState, useEffect } from "react";
import { Dialog, FormGroup, Card } from "@blueprintjs/core";
import Flexbox from "flexbox-react";
import ScrollableDropdown from "../shared/ScrollableDropdown";
import ToastStore from "../../stores/ToastStore";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import {REVIEW_PERIOD, REVIEW_PERIOD_LABELS, REVIEW_PERIOD_FREQUENCY, PERIOD_MONTHS} from "../../static/Constant";

const CheckInFrequencySettingDialog = ({
  isOpen,
  onClose,
  goalDuration,
  goalFrequency,
  checkinFrequency,
  setCheckinFrequency,
  onSaveCheckinFrequency,
  onCancel,
}) => {
   console.log("goalDuration", goalDuration);
   console.log("goalFrequency", goalFrequency);
   console.log("checkinFrequency from dialog", checkinFrequency);
   console.log("availableCheckIns", availableCheckIns);

  /* Which frequencies are allowed based on Goal Cycle duration */
  const availableCheckIns = useMemo(() => {
    switch (goalDuration) {
      case REVIEW_PERIOD.ANNUAL:
        return [
          REVIEW_PERIOD.HALF_YEARLY,
          REVIEW_PERIOD.QUARTERLY,
          REVIEW_PERIOD.BIMONTHLY,
          REVIEW_PERIOD.MONTHLY,
        ];

      case REVIEW_PERIOD.HALF_YEARLY:
        return [
          REVIEW_PERIOD.QUARTERLY,
          REVIEW_PERIOD.BIMONTHLY,
          REVIEW_PERIOD.MONTHLY,
        ];

      case REVIEW_PERIOD.QUARTERLY:
        return [
          REVIEW_PERIOD.MONTHLY,
        ];

      case REVIEW_PERIOD.BIMONTHLY:
        return [
          REVIEW_PERIOD.MONTHLY,
        ];

      case REVIEW_PERIOD.MONTHLY:
      default:
        return []; // <-- No check-ins possible
    }
  }, [goalDuration]);


  /* Generate Preview */
  const preview = useMemo(() => {
    if (checkinFrequency === "") return [];

    const MONTHS = [
      "",
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Months covered by selected Goal Cycle
    const selectedMonths = PERIOD_MONTHS[goalDuration]?.[goalFrequency-1] || [];
    if (!selectedMonths.length) return [];

    let groups = [];

    switch (checkinFrequency) {
      case REVIEW_PERIOD.MONTHLY:
        groups = selectedMonths.map(m => [m]);
        break;

      case REVIEW_PERIOD.BIMONTHLY:
        for (let i = 0; i < selectedMonths.length; i += 2) {
          groups.push(selectedMonths.slice(i, i + 2));
        }
        break;

      case REVIEW_PERIOD.QUARTERLY:
        for (let i = 0; i < selectedMonths.length; i += 3) {
          groups.push(selectedMonths.slice(i, i + 3));
        }
        break;

        case REVIEW_PERIOD.HALF_YEARLY:
          for (let i = 0; i < selectedMonths.length; i += 6) {
            groups.push(selectedMonths.slice(i, i + 6));
          }
        break;

      case REVIEW_PERIOD.ANNUAL:
        groups = [selectedMonths];
        break;

      default:
        return [];
    }

    return groups.map(group => {
      if (group.length === 1) {
        return MONTHS[group[0]];
      }

      return `${MONTHS[group[0]]} - ${MONTHS[group[group.length - 1]]}`;
    });

  }, [goalDuration, goalFrequency, checkinFrequency]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Create Check-ins"
      portalContainer={document.body}
    >
      <div style={{ padding: 20 }}>
        <FormGroup label="Goal Cycle" style={{ marginBottom: 20 }} >
          {/* <Card style={{ borderRadius: "8px" }}> */}
          <Flexbox justifyContent="space-between">
            <div>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "#5C7080",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                Duration
              </span>
              <div style={{ fontSize: "14px", fontWeight: 700 }} >
                {REVIEW_PERIOD_LABELS[goalDuration]}</div>
            </div>

            <div>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "#5C7080",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                Period
              </span>
              <div style={{ fontSize: "14px", fontWeight: 700 }} >
                {
                  REVIEW_PERIOD_FREQUENCY[
                    goalDuration
                  ].find(x => x.value === goalFrequency)?.label
                }
              </div>
            </div>
          </Flexbox>
          {/* </Card> */}
        </FormGroup>

        {availableCheckIns.length > 0 ? (
          <FormGroup label="Check-in Frequency">
            <ScrollableDropdown
              width={300}
              label={
                checkinFrequency === ""
                  ? "Select Frequency"
                  : REVIEW_PERIOD_LABELS[checkinFrequency]
              }
              items={availableCheckIns.map(value => ({
                id: value,
                title: REVIEW_PERIOD_LABELS[value],
              }))}
              getKey={item => item.id}
              getLabel={item => item.title}
              enableSearch={false}
              onSelect={item => setCheckinFrequency(item.id)}
            />
          </FormGroup>
        ) : (
          <Card style={{ marginBottom: 20 }}>
            <strong>No check-ins can be created.</strong>
            <div style={{ marginTop: 6, color: "#5C7080" }}>
              This goal cycle already runs monthly, so there is no smaller period
              available for creating check-ins.
            </div>
          </Card>
        )}

        {preview.length > 0 && (
          <Card
            style={{
              borderRadius: "8px",
              marginBottom: 20,
            }}
          >
            <h4 style={{ marginTop: 0, fontSize: "14px" }}>
              Check-in Summary
            </h4>

            <p>
              <strong>{preview.length}</strong>{" "}
              check-in{preview.length > 1 ? "s" : ""} will be
              created associated with this goal cycle once the goal cycle is published.
              <br />
              You can check in Check-ins settings tab after the goal cycle is published.
            </p>

            <div>
              <strong>Roadmap</strong>
              <Flexbox
                flexWrap="wrap"
                style={{
                  gap: "10px",
                }}
              >
                {preview.map(period => (
                  <div
                    key={period}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "6px 14px",
                      border: "1px solid #D8E1EA",
                      borderRadius: "999px",
                      background: "#FFFFFF",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#394B59",
                      boxShadow: "0 1px 2px rgba(16,22,26,.06)",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#2D72D2",
                        marginRight: "8px",
                      }}
                    />
                    {period}
                  </div>
                ))}
              </Flexbox>
              {/* <ul style={{ marginTop: 10 }}>
                {preview.map(period => (
                  <li key={period}>{period}</li>
                ))}
              </ul> */}
            </div>
          </Card>
        )}

        <Flexbox justifyContent="flex-start">
          <FilledButton
            text="Create Check-ins"
            disabled={
              availableCheckIns.length == 0 ||
              availableCheckIns.includes(checkinFrequency) === false ||
              checkinFrequency === ""
            }
            onClick={() =>
              onSaveCheckinFrequency(checkinFrequency)
            }
          />
          <OutlinedButton
            text="Cancel"
            onClick={onCancel}
            style={{ marginLeft: 10 }}
          />
        </Flexbox>
      </div>
    </Dialog>
  );
};

export default CheckInFrequencySettingDialog;